import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmployeeModalProps } from './EmployeeModal.types';
import { CreateEmployeeData, UpdateEmployeeData, EmployeeFormErrors, EmployeePermissions } from '../../types/employee.types';
import { SystemPermissionsModal } from './SystemPermissionsModal';
import { LocationPermissionsModal } from './LocationPermissionsModal';
import { Settings, MapPin, ChevronRight } from 'lucide-react';
import { permissionsService } from '@/services/permissions.service';

const defaultPermissions: EmployeePermissions = {
  pos: false,
  inventory: false,
  reports: false,
  employees: false,
  cashRegister: false,
  products: false,
  settings: false,
  locations: []
};

// Function to parse permissions from API format (string array) to EmployeePermissions
const parseEmployeePermissions = (permissions: any): EmployeePermissions => {
  // Handle case where permissions is already parsed
  if (typeof permissions === 'object' && permissions.pos !== undefined) {
    return {
      pos: permissions.pos || false,
      inventory: permissions.inventory || false,
      reports: permissions.reports || false,
      employees: permissions.employees || false,
      cashRegister: permissions.cashRegister || false,
      products: permissions.products || false,
      settings: permissions.settings || false,
      locations: Array.isArray(permissions.locations) ? permissions.locations : []
    };
  }

  // Handle JSON string format from API
  let permissionArray: string[] = [];
  try {
    if (typeof permissions === 'string') {
      permissionArray = JSON.parse(permissions);
    } else if (Array.isArray(permissions)) {
      permissionArray = permissions;
    }
  } catch (error) {
    console.error('Error parsing permissions:', error);
    return defaultPermissions;
  }

  return {
    pos: permissionArray.includes('VIEW_POS') || permissionArray.includes('CREATE_SALES'),
    inventory: permissionArray.includes('VIEW_INVENTORY') || permissionArray.includes('MANAGE_INVENTORY'),
    reports: permissionArray.includes('VIEW_REPORTS') || permissionArray.includes('MANAGE_REPORTS'),
    employees: permissionArray.includes('VIEW_EMPLOYEES') || permissionArray.includes('MANAGE_EMPLOYEES') || permissionArray.includes('CREATE_EMPLOYEES') || permissionArray.includes('UPDATE_EMPLOYEES') || permissionArray.includes('DELETE_EMPLOYEES'),
    cashRegister: permissionArray.includes('MANAGE_CASH_REGISTER') || permissionArray.includes('VIEW_CASH_REGISTER'),
    products: permissionArray.includes('MANAGE_PRODUCTS') || permissionArray.includes('VIEW_PRODUCTS'),
    settings: permissionArray.includes('MANAGE_SYSTEM') || permissionArray.includes('SYSTEM_SETTINGS'),
    locations: [] // Por ahora locations vacío, se puede expandir si es necesario
  };
};

export const EmployeeModal: React.FC<EmployeeModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  positions,
  locations,
  employee,
  loading = false
}) => {
  const isEditMode = !!employee;

  const [formData, setFormData] = useState<CreateEmployeeData>({
    firstName: '',
    lastName: '',
    dni: '',
    email: '',
    phone: '',
    address: '', // Optional but can be empty string
    positionId: '',
    salary: undefined, // Optional - undefined instead of 0
    locationId: '',
    permissions: defaultPermissions,
    password: ''
  });

  const [errors, setErrors] = useState<EmployeeFormErrors>({});
  const [isSystemPermissionsOpen, setIsSystemPermissionsOpen] = useState(false);
  const [isLocationPermissionsOpen, setIsLocationPermissionsOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [systemPermissionsCount, setSystemPermissionsCount] = useState(0);

  useEffect(() => {
    if (employee) {
      setFormData({
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        dni: employee.dni || '',
        email: employee.email || '',
        phone: employee.phone || '',
        address: employee.address || '',
        positionId: employee.position?.id ? String(employee.position.id) : '',
        salary: employee.salary || 0,
        locationId: employee.locationId || '',
        permissions: employee.permissions ? parseEmployeePermissions(employee.permissions) : defaultPermissions,
        password: ''
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        dni: '',
        email: '',
        phone: '',
        address: '', // Optional but can be empty string
        positionId: '',
        salary: undefined, // Optional - undefined instead of 0
        locationId: '',
        permissions: defaultPermissions,
        password: ''
      });
    }
    setErrors({});
    setServerError(null); // Clear server error when opening/closing modal

    // Load actual system permissions from API for existing employees
    if (employee?.id && isOpen) {
      loadEmployeePermissions(employee.id);
    } else {
      setSystemPermissionsCount(0);
    }
  }, [employee, positions, isOpen]);

  const loadEmployeePermissions = async (employeeId: number) => {
    try {
      const response = await permissionsService.getEmployeePermissions(employeeId);
      const systemPermissions = response?.systemPermissions || [];
      const count = systemPermissions.length;
      setSystemPermissionsCount(count);

      // Sync permissions with formData based on actual employee permissions
      const permissionFlags: Partial<EmployeePermissions> = {
        pos: systemPermissions.some((p: any) => p.module === 'pos' || p.code?.includes('pos')),
        inventory: systemPermissions.some((p: any) => p.module === 'inventory' || p.code?.includes('inventory')),
        reports: systemPermissions.some((p: any) => p.module === 'reports' || p.code?.includes('reports')),
        employees: systemPermissions.some((p: any) => p.module === 'employees' || p.code?.includes('employees')),
        cashRegister: systemPermissions.some((p: any) => p.module === 'cash_register' || p.code?.includes('cash_register')),
        products: systemPermissions.some((p: any) => p.module === 'products' || p.code?.includes('products')),
        settings: systemPermissions.some((p: any) => p.module === 'settings' || p.code?.includes('settings') || p.module === 'system_parameters'),
      };

      // Update formData with actual permissions
      setFormData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          ...permissionFlags
        }
      }));

      console.log('✅ Loaded employee permissions:', permissionFlags);
    } catch (error) {
      console.error('Error loading employee permissions:', error);
      setSystemPermissionsCount(0);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: EmployeeFormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Los apellidos son requeridos';
    }

    if (!formData.dni.trim()) {
      newErrors.dni = 'El DNI es requerido';
    } else if (!/^\d{8}$/.test(formData.dni)) {
      newErrors.dni = 'El DNI debe tener 8 dígitos';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    }

    if (!isEditMode && !formData.password?.trim()) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password && formData.password.trim() && formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!formData.positionId) {
      newErrors.positionId = 'El cargo es requerido';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Clear server error before attempting to save
    setServerError(null);

    try {
      if (isEditMode && employee) {
        const updateData: UpdateEmployeeData = { ...formData };
        // Only include password if it's provided
        if (!updateData.password?.trim()) {
          delete updateData.password;
        }
        // Permissions (system + locations) are saved separately via their dedicated modals
        delete updateData.permissions;
        await onUpdate(employee.id, updateData);
      } else {
        await onCreate(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving employee:', error);
      // Set server error to display in the modal
      if (error instanceof Error) {
        setServerError(error.message);
      } else {
        setServerError('Error inesperado al guardar el empleado');
      }
    }
  };

  const handleInputChange = (field: keyof CreateEmployeeData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (errors[field as keyof EmployeeFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
    
    // Clear server error when user starts typing
    if (serverError) {
      setServerError(null);
    }
  };

  const handlePermissionChange = (permission: keyof EmployeePermissions, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: checked
      }
    }));
  };

  const handleLocationPermissionChange = (locationId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        locations: checked 
          ? [...(prev.permissions.locations || []), locationId]
          : (prev.permissions.locations || []).filter(id => id !== locationId)
      }
    }));
  };

  const getSystemPermissionsCount = () => {
    // Use the count from API which reflects actual permissions in database
    return systemPermissionsCount;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Editar Empleado' : 'Nuevo Empleado'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'Actualiza la información del empleado'
              : 'Completa los datos para crear un nuevo empleado'
            }
          </DialogDescription>
        </DialogHeader>

        {/* Server Error Message */}
        {serverError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">
                  {serverError}
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">Nombre</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={errors.firstName ? 'border-red-500' : ''}
              />
              {errors.firstName && (
                <p className="text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Apellidos</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={errors.lastName ? 'border-red-500' : ''}
              />
              {errors.lastName && (
                <p className="text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dni">DNI</Label>
              <Input
                id="dni"
                value={formData.dni}
                onChange={(e) => handleInputChange('dni', e.target.value)}
                className={errors.dni ? 'border-red-500' : ''}
                maxLength={8}
              />
              {errors.dni && (
                <p className="text-sm text-red-600">{errors.dni}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Teléfono</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={errors.phone ? 'border-red-500' : ''}
            />
            {errors.phone && (
              <p className="text-sm text-red-600">{errors.phone}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">
              {isEditMode ? 'Nueva Contraseña (opcional)' : 'Contraseña'}
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password || ''}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={errors.password ? 'border-red-500' : ''}
              placeholder={isEditMode ? 'Dejar vacío para no cambiar' : ''}
            />
            {errors.password && (
              <p className="text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Dirección (opcional)</Label>
            <Input
              id="address"
              value={formData.address ?? ''}
              onChange={(e) => handleInputChange('address', e.target.value)}
              className={errors.address ? 'border-red-500' : ''}
              placeholder="Dirección del empleado"
            />
            {errors.address && (
              <p className="text-sm text-red-600">{errors.address}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="salary">Salario (opcional)</Label>
            <Input
              id="salary"
              type="number"
              value={formData.salary ?? ''}
              onChange={(e) => handleInputChange('salary', e.target.value ? parseFloat(e.target.value) : undefined)}
              className={errors.salary ? 'border-red-500' : ''}
              placeholder="0.00"
            />
            {errors.salary && (
              <p className="text-sm text-red-600">{errors.salary}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="position">Cargo</Label>
            <Select
              value={formData.positionId ? String(formData.positionId) : ''}
              onValueChange={(value) => handleInputChange('positionId', value)}
            >
              <SelectTrigger className={errors.positionId ? 'border-red-500' : ''}>
                <SelectValue placeholder="Selecciona un cargo" />
              </SelectTrigger>
              <SelectContent>
                {positions && positions.length > 0 ? positions.map((position) => (
                  <SelectItem key={position.id} value={String(position.id)}>
                    {position.name}
                  </SelectItem>
                )) : null}
              </SelectContent>
            </Select>
            {errors.positionId && (
              <p className="text-sm text-red-600">{errors.positionId}</p>
            )}
          </div>


          {/* Permisos y Accesos */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Permisos y Accesos</Label>
            <div className="space-y-3">
              {/* Botón Permisos del Sistema */}
              <button
                type="button"
                onClick={() => setIsSystemPermissionsOpen(true)}
                className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Settings className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-sm font-medium text-gray-900">Permisos del Sistema</h4>
                    <p className="text-xs text-gray-500">
                      {getSystemPermissionsCount()} módulos seleccionados
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </button>

              {/* Botón Acceso a Sucursales */}
              <button
                type="button"
                onClick={() => setIsLocationPermissionsOpen(true)}
                className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <MapPin className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-sm font-medium text-gray-900">Acceso a Sucursales</h4>
                    <p className="text-xs text-gray-500">
                      {formData.permissions.locations?.length || 0} de {locations?.length || 0} sucursales
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          </div>
        </form>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-macartur-primary rounded-md hover:bg-macartur-accent1 disabled:opacity-50"
          >
            {loading ? 'Guardando...' : (isEditMode ? 'Actualizar' : 'Crear')}
          </button>
        </DialogFooter>
      </DialogContent>

      {/* Modal de Permisos del Sistema */}
      <SystemPermissionsModal
        isOpen={isSystemPermissionsOpen}
        onClose={() => setIsSystemPermissionsOpen(false)}
        permissions={formData.permissions}
        onPermissionChange={handlePermissionChange}
        onSave={() => {
          // Refresh employee permissions after saving to sync formData
          console.log('✅ Permissions saved successfully, refreshing and syncing');
          if (employee?.id) {
            loadEmployeePermissions(employee.id);
          }
          setIsSystemPermissionsOpen(false);
        }}
        employeeId={employee?.id}
      />

      {/* Modal de Acceso a Sucursales */}
      <LocationPermissionsModal
        isOpen={isLocationPermissionsOpen}
        onClose={() => setIsLocationPermissionsOpen(false)}
        permissions={formData.permissions}
        locations={locations}
        onLocationPermissionChange={handleLocationPermissionChange}
        onSave={() => {
          console.log('✅ Location access updated:', formData.permissions.locations);
          setIsLocationPermissionsOpen(false);
        }}
        employeeId={employee?.id}
      />
    </Dialog>
  );
};